/* eslint-disable no-plusplus */
import React, { useContext, useEffect, useRef, useState } from "react";
import { Box } from "@material-ui/core";
import { HotTable } from "@handsontable/react";
import { registerAllModules } from "handsontable/registry";
import { CellChange, CellValue } from "handsontable/common";
import { CellMeta } from "handsontable/settings";

import ErrorSnackbarContext from "../../../../../../../contexts/ErrorSnackbarContext";
import CustomLoading from "../../../../../../../components/Alert/CustomLoading";
import useEnv from "../../../../../../../hooks/env";
import useStyles from "./MatrixInputStyle";
import MatrixHotTableMapper, { MatrixHotTableData } from "../../utils/MatrixHotTableMapper";
import MatrixConfigurationApi from "../../../../../../../api/services/MatrixConfigurationApi";
import TestSeriesDTO from "../../../../../../../api/models/TestSeriesDTO";
import { HttpErrorType } from "../../../../../../../models/HttpErrorType";
import customNumericRenderer from "./cells/MatrixInputCustomNumericRenderer";
import MatrixInputCustomNumericEditor from "./cells/MatrixInputCustomNumericEditor";
import { MatrixParameterValue } from "./interfaces/MatrixInputCell";
import customNumericValidator from "./cells/MatrixInputCustomNumericValidator";
import CustomTextValidator from "./cells/MatrixInputCustomTextValidator";
import {
  InputMatrixUpdate,
  InputMatrixRepetitionRequest,
} from "../../../../../../../api/models/test-request/TestRequestInputMatrix";
import customTextRenderer from "./cells/MatrixInputCustomTextRenderer";
import customDropdownRenderer from "./cells/MatrixInputCustomDropdownRenderer";
import CustomDropdownEditor from "./cells/MatrixInputCustomDropdownEditor";
import dropdownCustomValidator from "./cells/MatrixInputCustomDropdownValidator";
import "handsontable/dist/handsontable.full.min.css";
import customHeaderRenderer from "./cells/MatrixInputHeaderRenderer";
import mergeCellsIfEqual from "./cells/MergeCellsIfEqual";
import customNullRepetitionRenderer from "./cells/MatrixInputNullRepetitionRenderer";
import MatrixInputDialog from "./components/MatrixInputDialog";
import CustomTextEditor from "./cells/MatrixInputCustomTextEditor";
import { MatrixInputCellFeedbackProperties } from "./cells/MatrixInputCellFeedback";

// register Handsontable's modules
registerAllModules();

const CELL_SAVE_STATE = {
  QUEUED: "QUEUED",
  SAVING: "SAVING",
  SAVED: "SAVED",
  ERROR: "ERROR",
} as const;

type CellSaveState = typeof CELL_SAVE_STATE[keyof typeof CELL_SAVE_STATE];

interface CellErrorInfo {
  errorCode?: string;
  errorMessage?: string;
}

interface CellSaveStatus {
  state: CellSaveState;
  errorInfo?: CellErrorInfo;
}

export interface MatrixInputDialogProps {
  testSeries: TestSeriesDTO;
  testCaseIds: number[];
  readOnly: boolean;
  open: boolean;
  handleDialogOpen: (v: boolean) => void;
  handleOpenConfiguration: (v: boolean) => void;
}

const MatrixConfigurationDialog: React.FC<MatrixInputDialogProps> = (props) => {
  const UPDATE_DEBOUNCE_MS = 500;

  const { api } = useEnv();
  const matrixConfigApi = new MatrixConfigurationApi(api);
  const classes = useStyles();

  const hotTableRef = useRef<HotTable>(null);
  const copiedCellsRef = useRef<CellValue[][]>([]);
  const pendingUpdatesRef = useRef<Record<string, InputMatrixRepetitionRequest>>({});
  const flushTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cellSaveStatusRef = useRef<Record<string, CellSaveStatus>>({});
  const validationErrorRef = useRef<Record<string, string>>({});

  const { handleHttpError, showSnackbar } = useContext(ErrorSnackbarContext);

  const { open, handleDialogOpen, handleOpenConfiguration, testSeries, testCaseIds, readOnly } = props;

  const [loading, setLoading] = useState<boolean>(true);
  const [hotTableData, setHotTableData] = useState<MatrixHotTableData>();

  const createCellKey = (testResultRowId?: number, parameterValueId?: number) => {
    if (testResultRowId === undefined || parameterValueId === undefined) {
      return undefined;
    }

    return `${testResultRowId}-${parameterValueId}`;
  };

  const createUpdateKey = (update: InputMatrixRepetitionRequest) =>
    createCellKey(update.testResultRowId, update.parameterValue?.id);

  const getValidationError = (cellKey?: string): string | undefined => {
    if (!cellKey) return undefined;
    return validationErrorRef.current[cellKey];
  };

  const setValidationError = (cellKey: string, errorMessage?: string) => {
    if (errorMessage) {
      validationErrorRef.current[cellKey] = errorMessage;
    } else {
      delete validationErrorRef.current[cellKey];
    }
  };

  const setCellSaveState = (cellKey: string, state: CellSaveState, errorInfo?: CellErrorInfo) => {
    if (state === CELL_SAVE_STATE.ERROR && errorInfo) {
      cellSaveStatusRef.current[cellKey] = { state, errorInfo };
    } else {
      cellSaveStatusRef.current[cellKey] = { state };
    }
  };

  const flushPendingUpdates = async () => {
    if (readOnly) {
      pendingUpdatesRef.current = {};
      return;
    }

    const updates = Object.values(pendingUpdatesRef.current);
    if (!updates.length) {
      return;
    }

    // Mark all updates as SAVING
    updates.forEach((update) => {
      const cellKey = createCellKey(update.testResultRowId, update.parameterValue.id);
      if (cellKey) {
        setCellSaveState(cellKey, CELL_SAVE_STATE.SAVING);
      }
    });

    pendingUpdatesRef.current = {};

    try {
      const { data } = await matrixConfigApi.updateInputMatrix(testSeries.id, testSeries.version, updates, testCaseIds);
      const results: InputMatrixUpdate["results"] = data.results || [];

      // Process results from backend
      const errorCount = results.filter((result) => result.status === "ERROR").length;

      // Update cell states based on results
      if (results.length) {
        results.forEach((result) => {
          const cellKey = createCellKey(result.testResultRowId, result.parameterValueId);
          if (cellKey) {
            if (result.status === "SUCCESS") {
              setCellSaveState(cellKey, CELL_SAVE_STATE.SAVED);
            } else {
              setCellSaveState(cellKey, CELL_SAVE_STATE.ERROR, {
                errorCode: result.errorCode,
                errorMessage: result.errorMessage,
              });
            }
          }
        });
      }
      hotTableRef.current?.hotInstance?.render();

      // Keep current editor values as source of truth during typing to avoid stale-response flashes.

      // Only notify users when save errors exist; successful saves remain silent.
      if (errorCount > 0) {
        showSnackbar(`${errorCount} value(s) could not be saved. Please correct highlighted cells.`, "error");
      }
    } catch (error) {
      // Return failed updates to queue
      updates.forEach((update) => {
        const cellKey = createCellKey(update.testResultRowId, update.parameterValue.id);
        const updateKey = createUpdateKey(update);
        if (cellKey && updateKey) {
          setCellSaveState(cellKey, CELL_SAVE_STATE.QUEUED);
          pendingUpdatesRef.current[updateKey] = update;
        }
      });
      hotTableRef.current?.hotInstance?.render();
      handleHttpError(error as unknown as HttpErrorType);
    }
  };

  const scheduleFlush = () => {
    if (flushTimeoutRef.current) {
      clearTimeout(flushTimeoutRef.current);
    }

    flushTimeoutRef.current = setTimeout(() => {
      flushPendingUpdates().catch(() => undefined);
    }, UPDATE_DEBOUNCE_MS);
  };

  const createCells = (row: number, col: number): CellMeta => {
    const cellProperties: CellMeta = {};
    if (!hotTableData) {
      return cellProperties;
    }

    const cellData = hotTableData[row][col];

    if (!cellData) {
      cellProperties.readOnly = true;
      cellProperties.renderer = customNullRepetitionRenderer;
      return cellProperties;
    }

    if (typeof cellData && cellData === "") {
      cellProperties.readOnly = true;
    }
    if (typeof cellData === "string") {
      cellProperties.editor = false;
      cellProperties.renderer = customHeaderRenderer;
    }
    if (typeof cellData === "object") {
      // Store cell save state in meta for renderer to use
      const cellKey = createCellKey(cellData.testResultRowId, cellData.parameterValue.id);
      (cellProperties as MatrixInputCellFeedbackProperties).cellSaveState = cellKey
        ? cellSaveStatusRef.current[cellKey]
        : undefined;
      // Backend error takes priority; fall back to client-side validation error.
      const errorMessage = cellKey
        ? cellSaveStatusRef.current[cellKey]?.errorInfo?.errorMessage ?? getValidationError(cellKey)
        : undefined;
      (cellProperties as MatrixInputCellFeedbackProperties).validationError = errorMessage;

      if (cellData.parameterValue.type === "NUMBER") {
        cellProperties.editor = MatrixInputCustomNumericEditor as unknown as CellMeta["editor"];
        cellProperties.renderer = customNumericRenderer;
        cellProperties.validator = customNumericValidator;
      }
      if (cellData.parameterValue.type === "ENUM") {
        cellProperties.type = "dropdown";
        cellProperties.renderer = customDropdownRenderer;
        cellProperties.validator = dropdownCustomValidator;
        cellProperties.editor = CustomDropdownEditor as unknown as CellMeta["editor"];
      }
      if (cellData.parameterValue.type === "TEXT") {
        cellProperties.renderer = customTextRenderer;
        cellProperties.editor = CustomTextEditor as unknown as CellMeta["editor"];
        cellProperties.validator = CustomTextValidator;
      }
    }
    return cellProperties;
  };

  const handleCopyingData = (data: CellValue[][]) => {
    copiedCellsRef.current = data;
  };

  const handlePastingData = () => {
    if (readOnly) {
      return;
    }

    if (hotTableRef?.current?.hotInstance && copiedCellsRef.current) {
      const selectedCells = hotTableRef.current.hotInstance.getSelectedLast();
      if (selectedCells) {
        const [startRow, startCol] = selectedCells;
        copiedCellsRef.current.forEach((row, rowIndex) => {
          row.forEach((cellData, colIndex) => {
            const targetRow = startRow + rowIndex;
            const targetCol = startCol + colIndex;
            if (hotTableRef.current?.hotInstance) {
              hotTableRef.current.hotInstance.setDataAtCell(targetRow, targetCol, cellData);
            }
          });
        });
      }
    }
  };

  const handleCellChanged = (changes: CellChange[] | null, source?: string) => {
    if (readOnly || !changes || !hotTableRef?.current?.hotInstance) {
      return;
    }

    if (source === "loadData") {
      return;
    }

    let hasQueuedUpdate = false;

    changes.forEach(([row, col, oldValue, newValue]) => {
      const hotInstance = hotTableRef.current?.hotInstance;
      const columnIndex = Number(col);
      const meta = hotInstance?.getCellMeta(row, columnIndex);

      if (
        meta?.valid &&
        typeof oldValue === "object" &&
        oldValue?.parameterValue &&
        typeof newValue === "object" &&
        newValue?.parameterValue
      ) {
        const updatedCell: InputMatrixRepetitionRequest = {
          ...oldValue,
          parameterValue: {
            ...oldValue.parameterValue,
            value: newValue.parameterValue.value,
          } as InputMatrixRepetitionRequest["parameterValue"],
        };

        const cellKey = createCellKey(updatedCell.testResultRowId, updatedCell.parameterValue.id);
        const updateKey = createUpdateKey(updatedCell);
        if (!cellKey || !updateKey) {
          return;
        }

        setValidationError(cellKey);
        hotInstance?.setCellMeta(row, columnIndex, "valid", true);
        setCellSaveState(cellKey, CELL_SAVE_STATE.QUEUED);

        pendingUpdatesRef.current[updateKey] = updatedCell;
        hasQueuedUpdate = true;
      }
    });

    if (hasQueuedUpdate) {
      hotTableRef.current.hotInstance.render();
      scheduleFlush();
    }
  };

  const handleAfterValidate = (isValid: boolean, value: unknown, row: number, prop: string | number) => {
    if (!hotTableRef.current?.hotInstance) {
      return;
    }

    const cellData = hotTableRef.current.hotInstance.getSourceDataAtCell(row, Number(prop)) as
      | MatrixParameterValue
      | string
      | undefined;
    if (!cellData || typeof cellData === "string" || !cellData.testResultRowId || !cellData.parameterValue?.id) {
      return;
    }

    const cellKey = createCellKey(cellData.testResultRowId, cellData.parameterValue.id);
    if (!cellKey) {
      return;
    }

    if (isValid) {
      setValidationError(cellKey);
      hotTableRef.current.hotInstance.setCellMeta(row, Number(prop), "valid", true);
      hotTableRef.current.hotInstance.setCellMeta(row, Number(prop), "validationError", undefined);
      hotTableRef.current.hotInstance.setCellMeta(row, Number(prop), "cellSaveState", undefined);
    } else {
      const validationMessage =
        cellData.parameterValue.type === "NUMBER"
          ? "Please enter a valid number."
          : cellData.parameterValue.type === "ENUM"
          ? "Please select a valid option."
          : "Please correct the value.";
      setValidationError(cellKey, validationMessage);
      hotTableRef.current.hotInstance.setCellMeta(row, Number(prop), "valid", false);
      hotTableRef.current.hotInstance.setCellMeta(row, Number(prop), "validationError", validationMessage);
    }

    hotTableRef.current.hotInstance.render();
  };

  const fetchMatrixData = async () => {
    try {
      const matrixConfigurationResponse = await matrixConfigApi.getMatrixConfiguration(
        testSeries.id,
        testSeries.version,
      );
      const inputMatrixResponse = await matrixConfigApi.getInputMatrix(testSeries.id, testSeries.version, testCaseIds);
      const hotMapper = new MatrixHotTableMapper(inputMatrixResponse.data, matrixConfigurationResponse.data);
      setHotTableData(hotMapper?.createHotData());
    } catch (error) {
      handleHttpError(error as HttpErrorType);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMatrixData();

    return () => {
      if (flushTimeoutRef.current) {
        clearTimeout(flushTimeoutRef.current);
      }
      flushPendingUpdates().catch(() => undefined);
    };
  }, []);

  if (loading) {
    return <CustomLoading />;
  }

  return (
    <MatrixInputDialog
      testSeriesName={testSeries.name}
      fullScreen
      open={open}
      onClose={() => handleDialogOpen(false)}
      handleOpenConfiguration={() => handleOpenConfiguration(true)}
    >
      <Box className={classes.scrollingContainer}>
        <HotTable
          height={1000}
          ref={hotTableRef}
          licenseKey="31e17-23a3c-2e0a0-94604-d6938"
          data={hotTableData}
          readOnly={readOnly}
          cells={createCells}
          afterChange={handleCellChanged}
          afterValidate={handleAfterValidate}
          afterCopy={handleCopyingData}
          afterPaste={handlePastingData}
          mergeCells={mergeCellsIfEqual(hotTableData!)}
        />
      </Box>
    </MatrixInputDialog>
  );
};

export default MatrixConfigurationDialog;
